
import VueApexCharts from "vue-apexcharts";
import { Component, Vue } from "vue-property-decorator";
import axios from "axios";

// ─── Module-level cache: survives tab switching without re-fetching ──────────
interface AnalyticsCache {
  hostname: string;
  dateFrom: string;
  dateTo: string;
  timestamp: Date;
  daily: Record<string, number>;
  monthly: Record<string, number>;
  country: Record<string, number>;
  version: Record<string, number>;
  features: Record<string, number>;
  registrations: Record<string, number>;
}
let _cache: AnalyticsCache | null = null;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes in-memory freshness
const CACHE_KEY = "ers_ua_cache_v1"; // sessionStorage key

// ─── ETag store: url → last ETag value received ───────────────────────────
interface ETagStore {
  [url: string]: string;
}
let _etagMap: ETagStore = {};

// ─── sessionStorage helpers ───────────────────────────────────────────────
function saveToStorage(c: AnalyticsCache): void {
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ ...c, timestamp: c.timestamp.toISOString() }),
    );
  } catch {
    /* quota exceeded — silently skip */
  }
}
function loadFromStorage(): AnalyticsCache | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as any;
    p.timestamp = new Date(p.timestamp);
    return p as AnalyticsCache;
  } catch {
    return null;
  }
}

// ─── Adaptive X-axis: smart labels based on how many data points exist ─────────
const MONTH_ABBR = [
  "",
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function adaptiveXAxis(labels: string[]): Record<string, any> {
  const n = labels.length;
  if (n <= 14) {
    // 7D / 14D — show every day, slight rotation
    return {
      categories: labels,
      labels: { rotate: -30, hideOverlappingLabels: true, maxHeight: 55 },
    };
  }
  if (n <= 31) {
    // 30D — show ~10 evenly spaced dates
    return {
      categories: labels,
      tickAmount: 10,
      labels: {
        rotate: -45,
        rotateAlways: true,
        hideOverlappingLabels: true,
        maxHeight: 65,
      },
    };
  }
  if (n <= 92) {
    // 90D — show ~weekly (13 labels)
    return {
      categories: labels,
      tickAmount: 13,
      labels: {
        rotate: -45,
        rotateAlways: true,
        hideOverlappingLabels: true,
        maxHeight: 65,
      },
    };
  }
  // 1Y+ (>92 days) — pre-computed sparse labels: first occurrence of each year-month
  // gets "Mon" or "Mon 'YY"; all other days get '' so they render invisible.
  // No tickAmount — ApexCharts renders non-empty labels only.
  return {
    categories: labels,
    labels: {
      rotate: -30,
      rotateAlways: false,
      hideOverlappingLabels: true,
      maxHeight: 42,
      style: { fontSize: "11px" },
    },
    axisTicks: { show: false },
  };
}

// ─── Shared colour palette (matches existing UserStatics / Dashboards) ───────
const CHART_COLORS = [
  "#1A9AFA",
  "#13d8aa",
  "#d4526e",
  "#f48024",
  "#546E7A",
  "#2b908f",
  "#33b2df",
  "#90ee7e",
  "#f9a3a4",
  "#69d2e7",
  "#A5978B",
  "#4ecdc4",
  "#ff6b6b",
  "#ffd93d",
  "#6bcb77",
];

@Component({
  components: { apexchart: VueApexCharts },
})
export default class UserAnalytics extends Vue {
  // ── State ──────────────────────────────────────────────────────────────────
  public dateFrom: string = "";
  public dateTo: string = "";
  public activePreset: number = 30;
  public isLoading: boolean = false;
  public hasData: boolean = false;
  public cacheTimestamp: string = "";
  public isCached: boolean = false;
  public loadError: string = "";
  public loadProgress: number = 0; // 0–100; updated as each of the 6 API calls completes

  // Cancel token: aborts in-flight requests when Load is clicked again before the previous batch completes
  private _cancelSource: any = null;
  private _tickTimer: any = null; // setInterval handle for cacheAge live updates
  private _tick: number = 0; // reactive counter — forces cacheAge getter to recompute every 30s

  public presets = [
    { days: 7, label: "7D" },
    { days: 30, label: "30D" },
    { days: 90, label: "90D" },
    { days: 365, label: "1Y" },
  ];

  // ── KPI ────────────────────────────────────────────────────────────────────
  public kpi = {
    peakDailyUsers: 0,
    avgDailyUsers: 0,
    activeDays: 0,
    newRegistrations: 0,
  };

  // ── Series data ────────────────────────────────────────────────────────────
  public dailySeries = [{ name: "Unique Users", data: [] as number[] }];
  public monthlySeries = [{ name: "Unique Users", data: [] as number[] }];
  public versionSeries = [{ name: "Users", data: [] as number[] }];
  public featureSeries = [{ name: "Unique Users", data: [] as number[] }];
  public registrationSeries = [
    { name: "New Registrations", data: [] as number[] },
  ];
  public countryChartSeries: number[] = [];

  // ── Chart options (defined once, categories updated on load) ──────────────
  public dailyChartOptions = {
    chart: {
      type: "area",
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
      animations: { enabled: true, easing: "easeinout", speed: 400 },
    },
    colors: ["#1A9AFA"],
    fill: {
      type: "gradient",
      gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05 },
    },
    stroke: { width: 2, curve: "smooth" },
    xaxis: {
      categories: [] as string[],
      title: { text: "Date" },
      labels: { rotate: -45, maxHeight: 60 },
    },
    yaxis: {
      title: { text: "Unique Users" },
      min: 0,
      forceNiceScale: true,
      labels: {
        formatter: (val: number) =>
          Number.isInteger(val) ? val.toString() : "",
      },
    },
    dataLabels: { enabled: false },
    tooltip: { enabled: true },
  };

  public monthlyChartOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
    },
    colors: ["#13d8aa"],
    plotOptions: { bar: { borderRadius: 3, distributed: false } },
    xaxis: {
      categories: [] as string[],
      title: { text: "" },
      labels: {
        rotate: -45,
        rotateAlways: true,
        hideOverlappingLabels: true,
        style: { fontSize: "11px" },
      },
    },
    yaxis: {
      title: { text: "Unique Users" },
      min: 0,
      forceNiceScale: true,
      labels: {
        formatter: (val: number) =>
          Number.isInteger(val) ? val.toString() : "",
      },
    },
    dataLabels: { enabled: false },
  };

  public countryChartOptions: any = {
    chart: { type: "donut" },
    labels: [] as string[],
    colors: CHART_COLORS,
    legend: { position: "bottom", fontSize: "11px" },
    plotOptions: { pie: { donut: { size: "55%" } } },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => val.toFixed(1) + "%",
    },
  };

  public versionChartOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
    },
    colors: CHART_COLORS,
    // Vertical columns — visually distinct from the horizontal Features bars
    plotOptions: {
      bar: { borderRadius: 4, distributed: true, columnWidth: "55%" },
    },
    xaxis: {
      categories: [] as string[],
      labels: {
        rotate: -35,
        hideOverlappingLabels: true,
        style: { fontSize: "11px" },
      },
      title: { text: "Version" },
    },
    yaxis: {
      title: { text: "Unique Users" },
      min: 0,
      forceNiceScale: true,
      labels: {
        formatter: (val: number) =>
          Number.isInteger(val) ? val.toString() : "",
      },
    },
    legend: { show: false },
    dataLabels: {
      enabled: true,
      style: { colors: ["#fff"], fontSize: "11px", fontWeight: "600" },
      formatter: (val: number) => val.toString(),
      dropShadow: { enabled: true },
    },
    tooltip: {
      y: { formatter: (val: number) => val + " unique users" },
    },
  };

  public featureChartOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
    },
    colors: CHART_COLORS,
    plotOptions: {
      bar: {
        barHeight: "75%",
        horizontal: true,
        distributed: true,
        dataLabels: { position: "bottom" },
      },
    },
    stroke: { width: 1, colors: ["#fff"] },
    xaxis: {
      categories: [] as string[],
      labels: { show: false },
      axisBorder: { show: false },
      title: {
        text: "Unique Users",
        style: { fontSize: "11px", fontWeight: 400 },
      },
    },
    // Show feature names on Y-axis — truncated to avoid overflow
    yaxis: {
      labels: {
        show: true,
        maxWidth: 220,
        style: { fontSize: "11px" },
        formatter: (val: string) =>
          typeof val === "string" && val.length > 35
            ? val.substring(0, 33) + "…"
            : val,
      },
    },
    legend: { show: false },
    dataLabels: {
      enabled: true,
      textAnchor: "end",
      style: { colors: ["#fff"], fontSize: "11px" },
      // Show only the count — feature name is already on the Y-axis
      formatter: (val: number) => val.toString(),
      offsetX: -4,
      dropShadow: { enabled: true },
    },
    tooltip: {
      y: { formatter: (val: number) => val + " unique users" },
    },
  };

  public registrationChartOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
    },
    colors: ["#fd7e14"],
    plotOptions: { bar: { borderRadius: 3 } },
    xaxis: {
      categories: [] as string[],
      title: { text: "Date" },
      labels: {
        rotate: -45,
        rotateAlways: true,
        hideOverlappingLabels: true,
        trim: true,
        maxHeight: 80,
      },
      // NOTE: tickAmount is set dynamically in renderFromCache via adaptiveXAxis — not hardcoded here
    },
    yaxis: {
      title: { text: "New Users" },
      min: 0,
      forceNiceScale: true,
      labels: {
        // Never show decimals — user counts are always whole numbers
        formatter: (val: number) =>
          Number.isInteger(val) ? val.toString() : "",
      },
    },
    dataLabels: { enabled: false },
  };

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  public mounted() {
    this.setPreset(30);
    // Restore in-memory cache from sessionStorage (survives page refresh within same tab)
    if (!_cache) {
      const stored = loadFromStorage();
      if (stored) _cache = stored;
    }
    this.loadAll(false);
    // Tick every 30s so the "X min ago" cache-age badge updates live
    this._tickTimer = setInterval(() => {
      this._tick++;
    }, 30_000);
  }

  public beforeDestroy() {
    if (this._tickTimer) clearInterval(this._tickTimer);
    if (this._cancelSource) this._cancelSource.cancel("Component destroyed");
  }

  // ── Helpers ────────────────────────────────────────────────────────────────
  public fmt(n: number): string {
    if (n == null || isNaN(n)) return "—";
    return n.toLocaleString();
  }

  public setPreset(days: number) {
    this.activePreset = days;
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    this.dateTo = end.toISOString().split("T")[0];
    this.dateFrom = start.toISOString().split("T")[0];
  }

  private get hostname(): string {
    return localStorage.hostMode || "ers";
  }

  private isCacheValid(forceRefresh: boolean): boolean {
    if (forceRefresh || !_cache) return false;
    if (_cache.hostname !== this.hostname) return false;
    if (_cache.dateFrom !== this.dateFrom || _cache.dateTo !== this.dateTo)
      return false;
    return Date.now() - _cache.timestamp.getTime() < CACHE_TTL_MS;
  }

  // ── Main load (all 6 API calls in parallel; resilient to partial failures) ──
  public async loadAll(forceRefresh: boolean) {
    if (!this.dateFrom || !this.dateTo) return;
    if (new Date(this.dateFrom) > new Date(this.dateTo)) {
      this.loadError = "Start date must be before the end date.";
      return;
    }

    // Serve from in-memory cache if still fresh — zero network activity
    if (this.isCacheValid(forceRefresh)) {
      this.renderFromCache(_cache!);
      this.isCached = true;
      return;
    }

    // Cancel any in-flight requests from a previous loadAll
    // (e.g. user changed dates and clicked Load again before the previous batch finished)
    if (this._cancelSource)
      this._cancelSource.cancel("Superseded by newer request");
    this._cancelSource = axios.CancelToken.source();
    const cancelToken = this._cancelSource.token;

    this.isLoading = true;
    this.isCached = false;
    this.loadError = "";
    this.loadProgress = 0;

    const host = this.hostname;
    const from = this.dateFrom;
    const to = this.dateTo;

    // Dynamic timeout: allow more time for wider date ranges on large datasets
    const rangeDays = Math.ceil(
      (new Date(to).getTime() - new Date(from).getTime()) / 86_400_000,
    );
    const timeoutMs =
      rangeDays > 90
        ? 180_000 // 3 min for 1-year / large ranges
        : rangeDays > 30
        ? 90_000 // 90 sec for 31–90 day ranges
        : 30_000; // 30 sec for ≤ 30 day ranges

    // Helper: attach ETag if we have one for this URL, accept 304 as valid
    const get = (url: string) =>
      axios
        .get(url, {
          timeout: timeoutMs,
          cancelToken,
          headers: _etagMap[url] ? { "If-None-Match": _etagMap[url] } : {},
          validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
        })
        .then((r) => {
          if (r.headers?.etag) _etagMap[url] = r.headers.etag;
          return r;
        });

    const urls = [
      `/api/v4/Logs/dailyusercounts/${host}/${from}/${to}`,
      `/api/v4/Logs/monthlyusers/${host}/${from}/${to}`,
      `/api/v4/Logs/UsersbyDemograpic/${host}/${from}/${to}`,
      `/api/v4/Logs/UsersbyVersion/${host}/${from}/${to}`,
      `/api/v4/Logs/UsersbyFeature/${host}/${from}/${to}`,
      `/api/v4/Logs/GetDailyRegisteredUserCounts/${host}/${from}/${to}`,
    ];
    try {
      // ── Promise.allSettled: if 1–2 endpoints fail, still show the rest ────
      // Each promise also increments loadProgress (0→100) so the UI shows % complete
      let _done = 0;
      const total = urls.length;
      const tracked = urls.map((url) =>
        get(url).then(
          (r) => {
            this.loadProgress = Math.round((++_done / total) * 100);
            return r;
          },
          (e) => {
            this.loadProgress = Math.round((++_done / total) * 100);
            throw e;
          },
        ),
      );
      const results = await Promise.allSettled(tracked);

      // If this batch was cancelled while waiting, discard results silently
      const allCancelled = results.every(
        (r) =>
          r.status === "rejected" &&
          axios.isCancel((r as PromiseRejectedResult).reason),
      );
      if (allCancelled) return;

      const pick = (
        result: PromiseSettledResult<any>,
        existing: Record<string, number>,
      ) => {
        if (result.status === "rejected") return existing; // keep previous data on failure
        const res = result.value;
        return res.status === 304 ? existing : res.data?.[0] ?? {};
      };

      _cache = {
        hostname: host,
        dateFrom: from,
        dateTo: to,
        timestamp: new Date(),
        daily: pick(results[0], _cache?.daily ?? {}),
        monthly: pick(results[1], _cache?.monthly ?? {}),
        country: pick(results[2], _cache?.country ?? {}),
        version: pick(results[3], _cache?.version ?? {}),
        features: pick(results[4], _cache?.features ?? {}),
        registrations: pick(results[5], _cache?.registrations ?? {}),
      };

      saveToStorage(_cache);
      this.renderFromCache(_cache);

      // Warn if some (but not all) endpoints failed — charts still show from other feeds
      const failed = results.filter(
        (r) =>
          r.status === "rejected" &&
          !axios.isCancel((r as PromiseRejectedResult).reason),
      ) as PromiseRejectedResult[];
      if (failed.length > 0 && failed.length < results.length) {
        this.loadError =
          `${failed.length} of 6 data feeds failed — showing partial data. ` +
          `(${failed[0].reason?.message || "network error"})`;
      } else if (failed.length === results.length) {
        throw failed[0].reason; // every request failed — surface as real error
      }
    } catch (err: any) {
      if (axios.isCancel(err)) return; // normal cancellation — not an error
      console.error("UserAnalytics load error:", err);
      if (err?.code === "ECONNABORTED" || err?.message?.includes("timeout")) {
        this.loadError =
          `Request timed out after ${timeoutMs / 1000}s. ` +
          `For large date ranges (1 year+) the database may need more time. ` +
          `Try a shorter range, or ask your DB admin to add indexes on (Hostname, Timestamp, UserID).`;
      } else if (err?.response?.status === 504) {
        this.loadError =
          "Gateway timeout (504) — the API server took too long. Try a shorter range.";
      } else {
        this.loadError = `Failed to load data: ${
          err?.message || "Network error"
        }. Check your connection and try again.`;
      }
    } finally {
      this.isLoading = false;
    }
  }

  // ── Render all charts from a cache entry (no extra API calls) ─────────────
  private renderFromCache(cache: AnalyticsCache) {
    // ── KPI calculations (derived from fetched data) ─────────────────────
    const dailyValues = Object.values(cache.daily).map((v) => Number(v));
    const activeDays = dailyValues.filter((v) => v > 0).length;
    const peak = dailyValues.length ? Math.max(...dailyValues) : 0;
    const avg = activeDays
      ? Math.round(dailyValues.reduce((a, b) => a + b, 0) / activeDays)
      : 0;
    const newRegs = (Object.values(cache.registrations) as number[]).reduce(
      (a, b) => a + Number(b),
      0,
    );

    this.kpi = {
      peakDailyUsers: peak,
      avgDailyUsers: avg,
      activeDays,
      newRegistrations: newRegs,
    };

    // ── Daily chart ──────────────────────────────────────────────────────
    const dailyKeys = Object.keys(cache.daily).sort();
    const n = dailyKeys.length;
    let dayLabels: string[];
    if (n > 92) {
      // Large range: one label per year-month; show year suffix on first label, Jan, or year change
      const seenYM = new Set<string>();
      let lastLabelYear = -1;
      dayLabels = dailyKeys.map((d) => {
        const dt = new Date(d);
        const m = dt.getUTCMonth(); // 0-based
        const y = dt.getUTCFullYear();
        const key = `${y}-${m}`;
        if (!seenYM.has(key)) {
          seenYM.add(key);
          const needsYear =
            lastLabelYear === -1 || m === 0 || y !== lastLabelYear;
          lastLabelYear = y;
          return needsYear
            ? `${MONTH_ABBR[m + 1]} '${String(y).slice(2)}`
            : MONTH_ABBR[m + 1];
        }
        return "";
      });
    } else {
      dayLabels = dailyKeys.map((d) => {
        const dt = new Date(d);
        return `${dt.getUTCMonth() + 1}/${dt.getUTCDate()}`;
      });
    }
    this.$set(
      this.dailySeries[0],
      "data",
      dailyKeys.map((k) => Number(cache.daily[k])),
    );
    const dailyAxis = adaptiveXAxis(dayLabels);
    this.dailyChartOptions = {
      ...this.dailyChartOptions,
      xaxis: {
        ...this.dailyChartOptions.xaxis,
        title: { text: n > 92 ? "" : "Date" },
        ...dailyAxis,
      },
    };

    // ── Monthly chart ────────────────────────────────────────────────────
    const monthlyKeys = Object.keys(cache.monthly).sort();
    // Format "2025-03" → "Mar '25" — shorter and consistent with daily chart labels
    const fmtMonthKey = (k: string) => {
      const [yr, mo] = k.split("-");
      return `${MONTH_ABBR[parseInt(mo, 10)]} '${yr.slice(2)}`;
    };
    this.$set(
      this.monthlySeries[0],
      "data",
      monthlyKeys.map((k) => Number(cache.monthly[k])),
    );
    this.$set(
      this.monthlyChartOptions.xaxis,
      "categories",
      monthlyKeys.map(fmtMonthKey),
    );
    this.monthlyChartOptions = Object.assign({}, this.monthlyChartOptions);

    // ── Country donut ────────────────────────────────────────────────────
    const countryEntries = Object.entries(cache.country)
      .sort((a, b) => Number(b[1]) - Number(a[1]))
      .slice(0, 12);
    this.countryChartSeries = countryEntries.map((e) => Number(e[1]));
    this.$set(
      this.countryChartOptions,
      "labels",
      countryEntries.map((e) => e[0] || "Unknown"),
    );
    this.countryChartOptions = Object.assign({}, this.countryChartOptions);

    // ── Version chart ────────────────────────────────────────────────────
    const versionEntries = Object.entries(cache.version)
      .sort((a, b) => Number(b[1]) - Number(a[1]))
      .slice(0, 10);
    // Extract just the version number after the last '/' (e.g. "EY-ERS-Addin/1.22.4.1" → "1.22.4.1")
    const vLabel = (key: string) => {
      const i = key.lastIndexOf("/");
      return i >= 0 ? key.substring(i + 1) : key;
    };
    this.$set(
      this.versionSeries[0],
      "data",
      versionEntries.map((e) => Number(e[1])),
    );
    this.$set(
      this.versionChartOptions.xaxis,
      "categories",
      versionEntries.map((e) => vLabel(e[0])),
    );
    this.versionChartOptions = Object.assign({}, this.versionChartOptions);

    // ── Feature horizontal bar ───────────────────────────────────────────
    const featureEntries = Object.entries(cache.features)
      .sort((a, b) => Number(b[1]) - Number(a[1]))
      .slice(0, 20);
    this.$set(
      this.featureSeries[0],
      "data",
      featureEntries.map((e) => Number(e[1])),
    );
    this.$set(
      this.featureChartOptions.xaxis,
      "categories",
      featureEntries.map((e) => e[0]),
    );
    this.featureChartOptions = Object.assign({}, this.featureChartOptions);

    // ── Registrations bar ────────────────────────────────────────────────
    const regKeys = Object.keys(cache.registrations).sort();
    const rn = regKeys.length;
    let regLabels: string[];
    if (rn > 92) {
      const seenYM = new Set<string>();
      let lastLabelYear = -1;
      regLabels = regKeys.map((d) => {
        const dt = new Date(d);
        const m = dt.getUTCMonth();
        const y = dt.getUTCFullYear();
        const key = `${y}-${m}`;
        if (!seenYM.has(key)) {
          seenYM.add(key);
          const needsYear =
            lastLabelYear === -1 || m === 0 || y !== lastLabelYear;
          lastLabelYear = y;
          return needsYear
            ? `${MONTH_ABBR[m + 1]} '${String(y).slice(2)}`
            : MONTH_ABBR[m + 1];
        }
        return "";
      });
    } else {
      regLabels = regKeys.map((d) => {
        const dt = new Date(d);
        return `${dt.getUTCMonth() + 1}/${dt.getUTCDate()}`;
      });
    }
    this.$set(
      this.registrationSeries[0],
      "data",
      regKeys.map((k) => Number(cache.registrations[k])),
    );
    const regAxis = adaptiveXAxis(regLabels);
    this.registrationChartOptions = {
      ...this.registrationChartOptions,
      xaxis: {
        ...this.registrationChartOptions.xaxis,
        title: { text: rn > 92 ? "" : "Date" },
        ...regAxis,
      },
    };

    // ── Cache badge ──────────────────────────────────────────────────────
    this.cacheTimestamp = cache.timestamp.toLocaleTimeString();
    this.hasData = true;
  }

  // ── Human-readable range size for timeout warning ──────────────────────────
  public get rangeWarning(): string {
    if (!this.dateFrom || !this.dateTo) return "";
    const days = Math.ceil(
      (new Date(this.dateTo).getTime() - new Date(this.dateFrom).getTime()) /
        86_400_000,
    );
    if (days > 180)
      return `⚠ ${days}-day range — first load may take up to 3 minutes on large datasets. Subsequent loads are instant from cache.`;
    if (days > 90)
      return `ℹ ${days}-day range — first load may take up to 90 seconds. Cached after first fetch.`;
    return "";
  }

  // ── Admin: clear all cached data and ETags ────────────────────────────────
  public clearCache(): void {
    _cache = null;
    _etagMap = {};
    sessionStorage.removeItem(CACHE_KEY);
    this.hasData = false;
    this.cacheTimestamp = "";
    this.isCached = false;
    this.loadError = "";
    this.kpi = {
      peakDailyUsers: 0,
      avgDailyUsers: 0,
      activeDays: 0,
      newRegistrations: 0,
    };
    this.dailySeries = [{ name: "Unique Users", data: [] as number[] }];
    this.monthlySeries = [{ name: "Unique Users", data: [] as number[] }];
    this.versionSeries = [{ name: "Users", data: [] as number[] }];
    this.featureSeries = [{ name: "Unique Users", data: [] as number[] }];
    this.registrationSeries = [
      { name: "New Registrations", data: [] as number[] },
    ];
    this.countryChartSeries = [];
  }

  // ── Human-readable age of the current cache entry ─────────────────────────
  public get cacheAge(): string {
    void this._tick; // reactive dependency — re-evaluates every 30s so "Xm ago" stays current
    if (!_cache) return "";
    const secs = Math.floor((Date.now() - _cache.timestamp.getTime()) / 1000);
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    return `${Math.floor(secs / 3600)}h ago`;
  }

  // ─── CSV Download ─────────────────────────────────────────────────────────
  // All data comes from the in-memory cache — zero extra API calls.
  private downloadCSV(type: string) {
    if (!_cache) return;
    const date = new Date().toISOString().slice(0, 10);

    const toCSV = (headers: string[], rows: (string | number)[][]) =>
      [
        headers.join(","),
        ...rows.map((r) =>
          r.map((v) => '"' + String(v).replace(/"/g, '""') + '"').join(","),
        ),
      ].join("\n");

    const trigger = (csv: string, filename: string) => {
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = Object.assign(document.createElement("a"), {
        href: url,
        download: filename,
      });
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };

    if (type === "daily" || type === "all") {
      const rows = Object.entries(_cache.daily)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([d, v]) => [d, v]);
      const csv = toCSV(["Date", "UniqueUsers"], rows);
      if (type === "daily") {
        trigger(csv, `daily-users-${date}.csv`);
        return;
      }
    }
    if (type === "monthly" || type === "all") {
      const rows = Object.entries(_cache.monthly)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([d, v]) => [d, v]);
      const csv = toCSV(["Month", "UniqueUsers"], rows);
      if (type === "monthly") {
        trigger(csv, `monthly-users-${date}.csv`);
        return;
      }
    }
    if (type === "country" || type === "all") {
      const rows = Object.entries(_cache.country)
        .sort((a, b) => b[1] - a[1])
        .map(([c, v]) => [c, v]);
      const csv = toCSV(["Country", "UniqueUsers"], rows);
      if (type === "country") {
        trigger(csv, `users-by-country-${date}.csv`);
        return;
      }
    }
    if (type === "version" || type === "all") {
      const rows = Object.entries(_cache.version)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([v, c]) => [v, c]);
      const csv = toCSV(["Version", "UniqueUsers"], rows);
      if (type === "version") {
        trigger(csv, `users-by-version-${date}.csv`);
        return;
      }
    }
    if (type === "features" || type === "all") {
      const rows = Object.entries(_cache.features)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 20)
        .map(([f, c]) => [f, c]);
      const csv = toCSV(["Feature", "UniqueUsers"], rows);
      if (type === "features") {
        trigger(csv, `top-features-${date}.csv`);
        return;
      }
    }
    if (type === "registrations" || type === "all") {
      const rows = Object.entries(_cache.registrations)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([d, v]) => [d, v]);
      const csv = toCSV(["Date", "NewRegistrations"], rows);
      if (type === "registrations") {
        trigger(csv, `new-registrations-${date}.csv`);
        return;
      }
    }

    // type === 'all': combine everything into one file
    const daily = Object.entries(_cache.daily)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([d, v]) => [d, v]);
    const monthly = Object.entries(_cache.monthly)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([d, v]) => [d, v]);
    const country = Object.entries(_cache.country)
      .sort((a, b) => b[1] - a[1])
      .map(([c, v]) => [c, v]);
    const version = Object.entries(_cache.version)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([v, c]) => [v, c]);
    const features = Object.entries(_cache.features)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 20)
      .map(([f, c]) => [f, c]);
    const regs = Object.entries(_cache.registrations)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([d, v]) => [d, v]);

    const combined = [
      "DAILY UNIQUE USERS",
      toCSV(["Date", "UniqueUsers"], daily),
      "",
      "MONTHLY UNIQUE USERS",
      toCSV(["Month", "UniqueUsers"], monthly),
      "",
      "USERS BY COUNTRY",
      toCSV(["Country", "UniqueUsers"], country),
      "",
      "USERS BY VERSION",
      toCSV(["Version", "UniqueUsers"], version),
      "",
      "TOP 20 FEATURES",
      toCSV(["Feature", "UniqueUsers"], features),
      "",
      "NEW REGISTRATIONS",
      toCSV(["Date", "NewRegistrations"], regs),
    ].join("\n");

    trigger(combined, `analytics-all-${date}.csv`);
  }

  // ─── Active User Details CSV ──────────────────────────────────────────────
  // Calls the backend once to get ID/email, Country, Version, RegisteredOn for
  // every distinct user active in the selected date range.
  private async downloadUserDetails() {
    if (!this.dateFrom || !this.dateTo) return;

    const hostname = this.hostname;

    this.isLoading = true;
    this.loadError = "";

    try {
      const resp = await axios.get(
        `/api/v4/Logs/ActiveUserDetails/` +
          `${hostname}/${this.dateFrom}/${this.dateTo}`,
        { timeout: 120_000 },
      );

      const activeUserIds: string[] = Array.isArray(resp.data)
        ? resp.data
            .filter((id: any) => typeof id === "string" && id.trim().length > 0)
            .map((id: string) => id.trim())
        : [];

      if (activeUserIds.length === 0) {
        this.loadError = "No active users found for the selected date range.";
        return;
      }

      const generatedDate = new Date().toISOString().slice(0, 10);

      const toCell = (value: string) => '"' + value.replace(/"/g, '""') + '"';

      const csv = ["ActiveUserID", ...activeUserIds.map(toCell)].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

      const url = URL.createObjectURL(blob);

      const anchor = Object.assign(document.createElement("a"), {
        href: url,
        download: `active-users-${generatedDate}.csv`,
      });

      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

      URL.revokeObjectURL(url);
    } catch (err: any) {
      this.loadError =
        `Active user CSV download failed: ` +
        `${err?.message || "Network error"}. Try again.`;
    } finally {
      this.isLoading = false;
    }
  }
}
